/**
 * ReprocessBanner Component
 *
 * Displays a persistent banner when a collection is being reprocessed.
 * Shown at the top of the collection detail page.
 */

import { useI18n } from '@/i18n';
import { Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';

export interface ReprocessBannerProps {
  /** Current reprocessing status: null=ready, 'in_progress', 'failed' */
  status: string | null;
  /** Callback when dismiss is clicked (only for 'failed' status) */
  onDismiss?: () => void;
}

export function ReprocessBanner({ status, onDismiss }: ReprocessBannerProps) {
  const { t } = useI18n();

  if (!status) return null;

  if (status === 'in_progress') {
    return (
      <div className="mb-4 flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-800 dark:bg-blue-900/20">
        <Loader2 className="h-5 w-5 animate-spin text-blue-600 dark:text-blue-400" />
        <div className="flex-1">
          <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
            {t('admin.knowledge.reprocess.inProgressTitle')}
          </p>
          <p className="text-xs text-blue-600 dark:text-blue-400">
            {t('admin.knowledge.reprocess.inProgressDesc')}
          </p>
        </div>
      </div>
    );
  }

  if (status === 'completed') {
    return (
      <div className="mb-4 flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 dark:border-green-800 dark:bg-green-900/20">
        <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
        <p className="flex-1 text-sm font-medium text-green-800 dark:text-green-200">
          {t('admin.knowledge.reprocess.completedTitle')}
        </p>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="mb-4 flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 dark:border-red-800 dark:bg-red-900/20">
        <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
        <div className="flex-1">
          <p className="text-sm font-medium text-red-800 dark:text-red-200">
            {t('admin.knowledge.reprocess.failedTitle')}
          </p>
          <p className="text-xs text-red-600 dark:text-red-400">
            {t('admin.knowledge.reprocess.failedDesc')}
          </p>
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="text-sm font-medium text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
          >
            {t('common.dismiss')}
          </button>
        )}
      </div>
    );
  }

  return null;
}

export default ReprocessBanner;
